import {
  collection,
  getDocs,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";
import { db2026 } from "../config/firebase";
import {
  getActiveDb,
  getActiveTeamMapping,
  getActivePoolMaxTeams,
} from "../config/tournamentRuntime";
import { scoresService } from "./scoresService";
import { teamMappingService as defaultTeamMapping } from "../utils/teamMapping";
import {
  normalizePoolTeamName,
  normalizePoolTeamNames,
} from "../utils/normalizePoolTeamNames";

const getDb = () => getActiveDb() || db2026;
const getTeamMapping = () => getActiveTeamMapping() || defaultTeamMapping;
const getPoolMaxTeams = () => getActivePoolMaxTeams();

const POOL_ENTRIES_COLLECTION = "pool_entries";

export const golfPoolService = {
  getPoolMaxTeams,

  // Validate entry has the expected number of teams for the active tournament.
  // Scoring still works for archive entries with a different count.
  isValidPoolEntryTeamCount(entry, poolMaxTeams = getPoolMaxTeams()) {
    return (
      Array.isArray(entry?.teams) && entry.teams.length === poolMaxTeams
    );
  },

  validatePoolEntryData(entryData, poolMaxTeams = getPoolMaxTeams()) {
    if (!entryData?.participant?.trim()) {
      throw new Error("Participant name is required");
    }
    if (!Array.isArray(entryData?.teams)) {
      throw new Error("Teams must be an array");
    }
    if (entryData.teams.length !== poolMaxTeams) {
      throw new Error(
        `Pool entries must include exactly ${poolMaxTeams} teams`
      );
    }
    const uniqueTeams = new Set(entryData.teams);
    if (uniqueTeams.size !== entryData.teams.length) {
      throw new Error("Each team can only be selected once");
    }
  },
  // Get all pool entries
  async getPoolEntries() {
    try {
      const entriesRef = collection(getDb(), POOL_ENTRIES_COLLECTION);
      const q = query(entriesRef, orderBy("timestamp", "desc"));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          teams: normalizePoolTeamNames(data.teams),
        };
      });
    } catch (error) {
      console.error("Error getting pool entries:", error);
      throw error;
    }
  },

  // Real-time listener for pool entries
  onPoolEntriesChange(callback, errorCallback) {
    const entriesRef = collection(getDb(), POOL_ENTRIES_COLLECTION);
    const q = query(entriesRef, orderBy("timestamp", "desc"));

    return onSnapshot(
      q,
      (querySnapshot) => {
        const entries = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            teams: normalizePoolTeamNames(data.teams),
          };
        });
        callback(entries);
      },
      (error) => {
        console.error("Pool entries listener error:", error);
        if (errorCallback) {
          errorCallback(error);
        }
      }
    );
  },

  // Calculate pool entry score based on selected teams
  calculatePoolEntryScore(entry, scores, selectedRound = 3) {
    const teams = getTeamMapping().getAllTeams();
    const entryTeams = normalizePoolTeamNames(entry.teams);
    let totalScore = 0;
    let totalHolesPlayed = 0;
    let teamsWithScores = 0;
    const teamScores = [];

    // Calculate score for each selected team
    entryTeams.forEach((teamName) => {
      // Find the team by distinguished name (legacy Opie aliases remapped)
      const team = teams.find(
        (t) => t.name === normalizePoolTeamName(teamName)
      );
      if (team) {
        let teamScore = 0;
        let holesPlayed = 0;

        if (selectedRound === 3) {
          // Cumulative score for both rounds
          const cumulative = scoresService.calculateCumulativeTotal(
            team.id,
            scores
          );
          teamScore = cumulative.total;
          holesPlayed = cumulative.holesPlayed;
        } else {
          // Score for specific round
          const roundScore = scoresService.calculateTeamTotal(
            team.id,
            selectedRound,
            scores
          );
          teamScore = roundScore.total;
          holesPlayed = roundScore.holesPlayed;
        }

        // Only include teams that have actually played holes
        if (holesPlayed > 0) {
          totalScore += teamScore;
          totalHolesPlayed += holesPlayed;
          teamsWithScores++;
          teamScores.push({
            teamName,
            score: teamScore,
            holesPlayed,
          });
        } else {
          // Add team to list but without contributing to score
          teamScores.push({
            teamName,
            score: 0,
            holesPlayed: 0,
          });
        }
      }
    });

    // Calculate projected score if not all teams have played
    // This helps with fair comparison when some teams haven't started
    let projectedScore = 0;
    if (teamsWithScores > 0 && teamsWithScores < entryTeams.length) {
      // Calculate average score per team that has played
      const avgScorePerTeam = totalScore / teamsWithScores;
      // Project total score for all selected teams
      projectedScore = Math.round(avgScorePerTeam * entryTeams.length);
    }

    return {
      entryId: entry.id,
      participant: entry.participant,
      email: entry.email,
      phone: entry.phone,
      teams: entryTeams,
      teamScores,
      totalScore,
      totalHolesPlayed,
      teamsWithScores,
      projectedScore,
      averageHolesPlayed:
        teamsWithScores > 0
          ? Math.floor(totalHolesPlayed / teamsWithScores)
          : 0,
    };
  },

  // Get pool leaderboard
  calculatePoolLeaderboard(entries, scores, selectedRound = 3) {
    const leaderboard = entries.map((entry) =>
      this.calculatePoolEntryScore(entry, scores, selectedRound)
    );

    // Sort by total score (lower is better in golf)
    // Only include entries with scores
    const withScores = leaderboard.filter((entry) => entry.teamsWithScores > 0);
    const withoutScores = leaderboard.filter(
      (entry) => entry.teamsWithScores === 0
    );

    withScores.sort((a, b) => {
      // Primary sort: by total score (lower is better)
      if (a.totalScore !== b.totalScore) {
        return a.totalScore - b.totalScore;
      }
      // Secondary sort: by total holes played (more is better for ties)
      return b.totalHolesPlayed - a.totalHolesPlayed;
    });

    // Add tie information - need to do this in a way that properly handles position propagation
    const withTieInfo = [];
    let currentPosition = 1;

    for (let index = 0; index < withScores.length; index++) {
      const entry = withScores[index];
      let isTied = false;

      // Check if tied with previous entry
      if (index > 0 && withScores[index - 1].totalScore === entry.totalScore) {
        // Use the same position as previous tied entry
        isTied = true;
      } else {
        // New score, so position is current index + 1
        currentPosition = index + 1;
      }

      // Check if tied with next entry
      if (
        index < withScores.length - 1 &&
        withScores[index + 1].totalScore === entry.totalScore
      ) {
        isTied = true;
      }

      // Leader is anyone in first place (could be multiple)
      const isLeader = currentPosition === 1;

      withTieInfo.push({
        ...entry,
        position: currentPosition,
        isTied,
        isLeader,
      });
    }

    // Sort entries without scores alphabetically
    withoutScores.sort((a, b) => a.participant.localeCompare(b.participant));

    return [...withTieInfo, ...withoutScores];
  },

  // Format score relative to par
  formatPoolScore(score, teamsWithScores, rounds = 1) {
    if (teamsWithScores === 0) return "--";

    const PAR = 72;
    // Only calculate par for teams that have actually played
    const totalPar = PAR * rounds * teamsWithScores;
    const diff = score - totalPar;
    if (diff === 0) return "E";
    if (diff > 0) return `+${diff}`;
    return diff.toString();
  },

  // Get pool entry status text
  getPoolEntryStatus(entry, selectedRound) {
    if (entry.totalHolesPlayed === 0) return "Not Started";

    // Show how many teams have started
    if (entry.teamsWithScores < entry.teams.length) {
      if (entry.teamsWithScores === 0) {
        return "No teams started";
      }
      if (selectedRound === 3) {
        // For cumulative view
        const avgHoles = entry.averageHolesPlayed;
        if (avgHoles > 18) {
          return `${entry.teamsWithScores}/${entry.teams.length} teams (R2)`;
        } else {
          return `${entry.teamsWithScores}/${entry.teams.length} teams (R1)`;
        }
      } else {
        return `${entry.teamsWithScores}/${entry.teams.length} teams`;
      }
    }

    if (selectedRound === 3) {
      // For cumulative view
      const maxHoles = 36 * entry.teams.length; // 36 holes (2 rounds) per team
      if (entry.totalHolesPlayed === maxHoles) return "Complete";

      // Calculate average progress
      const avgHoles = entry.averageHolesPlayed;
      if (avgHoles > 18) {
        return `R2 Avg Thru ${avgHoles - 18}`;
      } else if (avgHoles === 18) {
        return "R1 Complete";
      } else {
        return `R1 Avg Thru ${avgHoles}`;
      }
    } else {
      // For single round view
      const maxHoles = 18 * entry.teams.length;
      if (entry.totalHolesPlayed === maxHoles) return "Complete";

      const avgHoles = entry.averageHolesPlayed;
      return `Avg Thru ${avgHoles}`;
    }
  },

  // Get tie and prize information
  getTieInfo(leaderboard) {
    const leaders = leaderboard.filter(
      (entry) => entry.isLeader && entry.teamsWithScores > 0
    );
    const totalEntries = leaderboard.filter(
      (entry) => entry.teamsWithScores > 0
    ).length;

    if (leaders.length === 0) {
      return {
        hasWinners: false,
        winnerCount: 0,
        prizeShare: 0,
        message: "No entries with completed scores yet",
      };
    }

    const ENTRY_FEE = 25;
    const totalPot = totalEntries * ENTRY_FEE;
    const winnerPot = totalPot * 0.5; // 50% to winners
    const charityPot = totalPot * 0.5; // 50% to charity
    const prizePerWinner = Math.floor(winnerPot / leaders.length);

    return {
      hasWinners: true,
      winnerCount: leaders.length,
      prizeShare: prizePerWinner,
      totalPot,
      winnerPot,
      charityPot,
      leaders,
      message:
        leaders.length > 1
          ? `${leaders.length}-way tie for first place`
          : `${leaders[0].participant} leads!`,
    };
  },
};
